import {CalendarComponent} from "@/event/calendar/all/CalendarComponent.ts";
import {CalendarView, saveView} from "@/event/calendar/CalendarModel.ts";
import {ScrollComponent} from "@/event/calendar/dom/ScrollComponent.ts";
import {SplitComponent, SplitOptions} from "@/event/calendar/dom/SplitComponent.ts";
import {DebugPattern} from "@/event/calendar/dom/DebugPattern.ts";
import {DaysHeader} from "@/event/calendar/days/DaysHeader.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {Rect, Slot} from "@/event/calendar/Shapes.ts";
import {GRID_HEADER_HEIGHT} from "@/event/calendar/Constants.ts";
import {DaysWholeArea} from "@/event/calendar/days/DaysWholeArea.ts";
import {DaysTimedArea} from "@/event/calendar/days/DaysTimedArea.ts";
import {DaysCorner} from "@/event/calendar/days/DaysCorner.ts";
import {GutterSized} from "@/event/calendar/days/GutterSized.ts";
import {DaysWholeHeader} from "@/event/calendar/days/DaysWholeHeader.ts";
import {DaysTimedHeader} from "@/event/calendar/days/DaysTimedHeader.ts";
import {DaysTimedBody} from "@/event/calendar/days/DaysTimedBody.ts";
import {DaysWholeBody} from "@/event/calendar/days/DaysWholeBody.ts";
import {findNextMonday, findPrevMonday, mondayDate, newShifted, sameDay, shiftDate} from "@t/util/DateUtil.ts";
import {DaysTopHeader} from "@/event/calendar/days/DaysTopHeader.ts";
import {TOUCH} from "@/event/calendar/dom/Component.ts";

/******

 +-----------------------------------------------------------+
 | DaysCorner      | DaysTopHeader                           |  <--- top header DaysHeader
 +-----------------------------------------------------------+
 | DaysWholeHeader | DaysWholeBody                           |  <--- all-day events -- DaysWholeArea
 +-----------------------------------------------------------+
 | DaysTimedHeader | DaysTimedBody                           |  <--- timed events ---- DaysTimedArea
 |                 |                                         |
 |                 |                                         |
 +-----------------------------------------------------------+

 ******/

export class DaysComponent extends CalendarComponent {
	// private grid: DaysComponent;
	// private scroll: ScrollComponent;
	// private gutter: WeekGutterComponent;

	private corner: DaysCorner;
	private topHeader: DaysTopHeader;
	private topHeaderScroll: ScrollComponent;
	private header:DaysHeader;

	private wholeHeader: DaysWholeHeader;
	private wholeBody: DaysWholeBody;
	private wholeBodyScroll: ScrollComponent;
	private wholeArea: DaysWholeArea;

	private timedHeader: DaysTimedHeader;
	private timedHeaderScroll: ScrollComponent;
	private timedBody: DaysTimedBody;
	private timedBodyScroll: ScrollComponent;
	private timedArea: DaysTimedArea;

	private body: SplitComponent;

	private gutterWidth: number;
	private columns: DayColumns;


	constructor(view: CalendarView) {
		super(view);

		this.corner = new DaysCorner();
		this.topHeader = new DaysTopHeader(view);
		this.topHeaderScroll = new ScrollComponent(this.topHeader, true, false);
		this.header = new DaysHeader(this.corner, this.topHeaderScroll);

		this.append(this.header);

		this.wholeHeader = new DaysWholeHeader();
		this.wholeBody = new DaysWholeBody(view);
		this.wholeBodyScroll = new ScrollComponent(this.wholeBody, true, true).withScrollbars(TOUCH, false);
		this.wholeArea = new DaysWholeArea(this.wholeHeader, this.wholeBodyScroll, this.wholeBody);

		this.timedHeader = new DaysTimedHeader(view);
		this.timedHeaderScroll = new ScrollComponent(this.timedHeader, false);
		this.timedBody = new DaysTimedBody(view);
		this.timedBodyScroll = new ScrollComponent(this.timedBody, true, true, !TOUCH).withScrollbars(TOUCH, false);
		this.timedArea = new DaysTimedArea(this.timedHeader, this.timedHeaderScroll, this.timedBodyScroll);

		this.timedBodyScroll.syncWith(this.topHeaderScroll);
		this.timedBodyScroll.syncWith(this.wholeBodyScroll);
		this.topHeaderScroll.syncWith(this.wholeBodyScroll);

		this.timedHeaderScroll.syncWith(this.timedBodyScroll, false);

		const splitOptions:SplitOptions = {
			horizontal: false,
			min: 0.05,
			max: 0.7,
			initial: 0.08
		};
		if (view.saveName) splitOptions.saveAs = view.saveName + ".vsplit";
		this.body = new SplitComponent(this.wholeArea, this.timedArea, splitOptions);
		this.append(this.body);
	}

	calculateGutter(dc: DrawingContext, ...components: GutterSized[]) {
		let max = 0;
		for (let c of components) {
			const width = c.calcMinGutter(dc);
			if (width > max) max = width;
		}
		max = Math.ceil(max);
		for (let c of components) {
			c.gutterWidth = max;
		}
		this.gutterWidth = max;
	}

	placeChildren(dc: DrawingContext, cb: Rect) {
		this.calculateGutter(dc, this.corner, this.wholeHeader, this.timedHeader);

		this.header.x = cb.x;
		this.header.y = cb.y;
		this.header.width = cb.width
		this.header.height = GRID_HEADER_HEIGHT;

		this.body.x = cb.x;
		this.body.y = cb.y + GRID_HEADER_HEIGHT;
		this.body.width = cb.width;
		this.body.height = cb.height - GRID_HEADER_HEIGHT;
	}

	draw(dc: DrawingContext, area: Rect) {
		// Calculate before drawing
		const space = this.width - this.gutterWidth;
		const colWidth = space / weekGridDays(this.view);
		this.columns = calcDayColumns(colWidth, this.view, this.timedBodyScroll.scrollX, space);
		// Pass the precalculated metrics to child component
		this.topHeader.dayColumns = this.columns;
		this.wholeBody.dayColumns = this.columns;
		this.timedBody.dayColumns = this.columns;

		super.draw(dc, area);
	}

	moveToDate(d:Date, smooth=true) {
		this.timedBody.updateScrollForNewSelectedDate(d);
		this.view.focused = null;
		this.timedBodyScroll.scrollTo(0, this.timedBodyScroll.scrollY, smooth);
	}

	moveToToday(max: boolean = false) {
		let day = new Date();
		if (this.view.snapMonday && this.view.days >= 5) {
			const dow = day.getDay();
			if (!(dow === 0 && this.view.days <= 6 || dow === 6 && this.view.days === 5)) {
				// Shift only if the target date will be visible after monday-shift
				day = mondayDate(day);
			}
		}
		this.moveToDate(day);
	}

	moveToPrevious(max: boolean = false) {
		const wgd = weekGridDays(this.view);
		const alignToMonday = (wgd >= 5) && this.view.snapMonday;
		let anchorDate = this.timedBody.firstVisibleDate();
		if (max) anchorDate = newShifted(anchorDate, -1);
		else if (alignToMonday) anchorDate = findPrevMonday(anchorDate);
		else anchorDate = newShifted(anchorDate, -1 * wgd);
		this.moveToDate(anchorDate);
	}

	moveToNext(max: boolean = false) {
		const wgd = weekGridDays(this.view);
		const alignToMonday = (wgd >= 5) && this.view.snapMonday;
		let anchorDate = this.timedBody.firstVisibleDate();
		if (max) anchorDate = newShifted(anchorDate, 1);
		else if (alignToMonday) anchorDate = findNextMonday(anchorDate)
		else anchorDate = newShifted(anchorDate, wgd);
		this.moveToDate(anchorDate);
	}

	zoomIn(max = false) {
		const wgd = weekGridDays(this.view);
		if (wgd > 1) {
			this.view.days = max ? 1 : wgd - 1;
			saveView(this.view);
			this.dirty(true);
		}
	}

	zoomOut(max = false) {
		const wgd = weekGridDays(this.view);
		if (wgd < 14) {
			this.view.days = max ? (wgd < 7 ? 7 : 14) : wgd + 1;
			saveView(this.view);
			this.dirty(true);
		}
	}
}

export interface DayColumn {
	date: Date,
	today: boolean,		// is today
	selected: boolean,	// is focused day
	aligned: boolean,	// this day is at zero scroll position
	x: number,		// position of the column
	width: number	// width of the column
}

export interface DayColumns {
	/** First and last days of visible span */
	first: Date,
	last: Date,
	days: DayColumn[],
}

export function selectedDateToAligned(selected:Date, dayColumns = 1):Date {
	const f = new Date(selected); // normalized selected
	f.setHours(12, 0, 0, 0);	// align to noon
	// if (dayColumns >= 5) {
	// 	// We can hold the whole work, lets align to monday
	// 	let shiftBack:number;
	// 	switch (f.getDay()) {
	// 		case 0 /* sunday */: shiftBack = -1; break;	// to saturday
	// 		case 1 /* monday */: shiftBack = 0; break;
	// 		case 2 /* tuesday */: shiftBack = -1; break;
	// 		case 3 /* wednesday */: shiftBack = -2; break;
	// 		case 4 /* thursday */: shiftBack = -3; break;
	// 		case 5 /* friday */: shiftBack = -4; break;
	// 		case 6 /* saturday */: shiftBack = 0; break;
	// 	}
	// 	shiftDate(f, shiftBack);
	// } else if (dayColumns === 4) {
	// 	shiftDate(f, -1);	// include yesterday
	// }
	return f;
}

export function calcDayColumns(colWidth:number, view: CalendarView, scrollX: number, bodyWidth:number):DayColumns {
	const today = new Date();
	view.aligned = selectedDateToAligned(view.selected, weekGridDays(view));
	let f = new Date(view.aligned);

	// Now we have to find the first visible day
	const dayShift = Math.floor(scrollX / colWidth);	// number of days to shift
	shiftDate(f, dayShift);	// now f points to the first visible day @ left edge

	const cols:DayColumns = { first: new Date(f), last: f, days: [] };

	let dayLeftEdge = dayShift * colWidth;
	while (dayLeftEdge < (scrollX + bodyWidth)) {
		const colDate = new Date(f);
		const dcol:DayColumn = {
			date: colDate,
			today: sameDay(today, colDate),
			selected: sameDay(view.selected, colDate),
			aligned: sameDay(view.aligned, colDate),
			x: dayLeftEdge,
			width: colWidth
		};
		cols.days.push(dcol);
		cols.last = colDate;
		dayLeftEdge += colWidth;
		shiftDate(f);
	}

	view.focused = { start: cols.first, end: cols.last };
	view.store.needRange(cols.first, cols.last, view.saveName ?? "main");
	return cols;
}


export interface HasDayColumns {
	dayColumns: DayColumns;
}

export function weekGridDays(view:CalendarView):number {
	const wgd = view.days;
	if (typeof wgd != "number") return 3;
	if (wgd < 1) return 1;
	if (wgd > 14) return 14;
	return wgd;
}
